import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Building2,
  User,
  FileText,
  Image,
  Users,
  Edit,
  Loader2,
  FolderOpen,
  Hash,
} from "lucide-react";
import { CustomerFormDialog } from "@/components/Customers/CustomerFormDialog";
import { GalleryManager } from "@/components/Gallery/GalleryManager";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function CustomerDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [customer, setCustomer] = useState<any>(null);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [operators, setOperators] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const canManage = userRole === "admin" || userRole === "supervisor";

  useEffect(() => {
    if (id) {
      fetchCustomerData();
    }

    // Listen for customer updates
    const handleCustomerUpdate = () => {
      fetchCustomerData();
    };
    window.addEventListener('customer-updated', handleCustomerUpdate);

    return () => {
      window.removeEventListener('customer-updated', handleCustomerUpdate);
    };
  }, [id]);

  const fetchCustomerData = async () => {
    setLoading(true);
    try {
      // Fetch customer
      const { data: customerData, error: customerError } = await api
        .from("customers")
        .select("*")
        .eq("id", id)
        .single();

      if (customerError) throw customerError;
      setCustomer(customerData);

      // Fetch projects for this customer FIRST
      const { data: projectsData, error: projectsError } = await api
        .from("projects")
        .select("*")
        .eq("customer_id", id)
        .order("project_number", { ascending: false });

      if (projectsError) throw projectsError;
      setProjects(projectsData || []);

      // Fetch work orders for this customer (direct + via projects)
      const projectIds = (projectsData || []).map((p: any) => p.id);
      const { data: ordersData, error: ordersError } = await api
        .from("work_orders")
        .select("*, project:projects(name)")
        .or(projectIds.length > 0 
          ? "customer_id.eq." + id + ",project_id.in.(" + projectIds.join(",") + ")"
          : "customer_id.eq." + id)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (ordersError) throw ordersError;
      setWorkOrders(ordersData || []);

      // Fetch operators if admin/supervisor
      if (canManage) {
        const { data: operatorsData, error: operatorsError } = await api
          .from("profiles")
          .select("*, user_roles(role)")
          .eq("is_active", true)
          .order("full_name");

        if (operatorsError) throw operatorsError;
        setOperators(operatorsData || []);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      pending: "secondary",
      in_progress: "default",
      completed: "outline",
      cancelled: "outline",
    };
    
    const labels: Record<string, string> = {
      pending: "Pendiente",
      in_progress: "En Progreso",
      completed: "Completado",
      cancelled: "Cancelado",
    };

    return (
      <Badge variant={variants[status] || "secondary"}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!customer) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Cliente no encontrado</p>
          <Button onClick={() => navigate("/customers")} className="mt-4">
            Volver a Clientes
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/customers")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                {customer.company_name || `${customer.first_name || ""} ${customer.last_name || ""}`.trim()}
              </h1>
              <p className="text-muted-foreground mt-1">
                Detalles del cliente
              </p>
            </div>
          </div>
          {canManage && (
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-gradient-magma hover:shadow-magma-hover"
            >
              <Edit className="w-4 h-4 mr-2" />
              Editar Cliente
            </Button>
          )}
        </div>

        {/* Customer Info Card */}
        <Card className="glass-card p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-primary mt-1" />
              <div>
                <p className="text-sm text-muted-foreground">Contacto</p>
                <p className="font-medium">
                  {customer.first_name} {customer.last_name}
                </p>
              </div>
            </div>

            {customer.email && (
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium break-all">{customer.email}</p>
                </div>
              </div>
            )}

            {customer.phone && (
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Teléfono</p>
                  <p className="font-medium">{customer.phone}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-primary mt-1" />
              <div>
                <p className="text-sm text-muted-foreground">Estado</p>
                <Badge variant={customer.is_active ? "default" : "secondary"}>
                  {customer.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
            </div>
          </div>

          {customer.address && (
            <div className="mt-6 pt-6 border-t border-border/50">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Dirección</p>
                  <p className="font-medium">{customer.address}</p>
                </div>
              </div>
            </div>
          )}

          {customer.notes && (
            <div className="mt-6 pt-6 border-t border-border/50">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Notas</p>
                  <p className="font-medium whitespace-pre-wrap">{customer.notes}</p>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Tabs Section */}
        <Tabs defaultValue="projects" className="space-y-6">
          <TabsList>
            <TabsTrigger value="projects">
              <FolderOpen className="w-4 h-4 mr-2" />
              Proyectos ({projects.length})
            </TabsTrigger>
            <TabsTrigger value="orders">
              <FileText className="w-4 h-4 mr-2" />
              Órdenes de Trabajo ({workOrders.length})
            </TabsTrigger>
            <TabsTrigger value="gallery">
              <Image className="w-4 h-4 mr-2" />
              Galería
            </TabsTrigger>
            {canManage && (
              <TabsTrigger value="operators">
                <Users className="w-4 h-4 mr-2" />
                Operarios
              </TabsTrigger>
            )}
          </TabsList>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4">
            <Card className="glass-card">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Proyectos Asociados ({projects.length})
                </h3>
                {projects.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No hay proyectos para este cliente</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nº Proyecto</TableHead>
                        <TableHead>Código</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Fecha Inicio</TableHead>
                        <TableHead>Presupuesto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projects.map((project) => (
                        <TableRow key={project.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/projects`)}>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              P-{String(project.project_number).padStart(4, '0')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {project.reference_code && (
                              <Badge variant="secondary">{project.reference_code}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{project.name}</TableCell>
                          <TableCell>
                            <Badge variant={project.status === 'active' ? 'default' : project.status === 'completed' ? 'outline' : 'secondary'}>
                              {project.status === 'planning' ? 'Planificación' :
                               project.status === 'active' ? 'Activo' :
                               project.status === 'completed' ? 'Completado' :
                               project.status === 'paused' ? 'Pausado' :
                               project.status === 'cancelled' ? 'Cancelado' : project.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {project.start_date ? format(new Date(project.start_date), "dd MMM yyyy", { locale: es }) : '-'}
                          </TableCell>
                          <TableCell>
                            {project.budget ? `€${Number(project.budget).toLocaleString()}` : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Work Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            <Card className="glass-card">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Historial de Órdenes ({workOrders.length})
                </h3>
                {workOrders.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No hay órdenes de trabajo para este cliente</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Número</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Cantidad</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Fecha Creación</TableHead>
                        <TableHead>Prioridad</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {workOrders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="font-medium">
                            {order.order_number}
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              order.order_type === 'master' ? 'destructive' :
                              order.order_type === 'design' ? 'secondary' :
                              order.order_type === 'plotter_cut' ? 'outline' :
                              'default'
                            }>
                              {order.order_type === 'production' ? 'Producción' :
                               order.order_type === 'design' ? 'Diseño' :
                               order.order_type === 'plotter_cut' ? 'Corte' :
                               order.order_type === 'master' ? 'Máster' :
                               order.order_type || 'Normal'}
                            </Badge>
                          </TableCell>
                          <TableCell>{order.product_name}</TableCell>
                          <TableCell>{order.quantity}</TableCell>
                          <TableCell>{getStatusBadge(order.status)}</TableCell>
                          <TableCell>
                            {format(new Date(order.created_at), "dd MMM yyyy", { locale: es })}
                          </TableCell>
                          <TableCell>
                            <Badge variant={order.priority > 5 ? "default" : "secondary"}>
                              {order.priority}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <GalleryManager
              elementId={customer.id}
              elementType="customer"
              title="Galería del Cliente"
            />
          </TabsContent>

          {/* Operators Tab (Admin/Supervisor only) */}
          {canManage && (
            <TabsContent value="operators" className="space-y-4">
              <Card className="glass-card">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    Operarios Disponibles ({operators.length})
                  </h3>
                  {operators.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No hay operarios disponibles</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {operators.map((operator) => (
                        <Card key={operator.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{operator.full_name}</p>
                              {operator.tag && (
                                <Badge variant="outline" className="mt-1">
                                  {operator.tag}
                                </Badge>
                              )}
                              <p className="text-sm text-muted-foreground mt-2">
                                ID: {operator.employee_id || "N/A"}
                              </p>
                              {operator.area && (
                                <p className="text-xs text-muted-foreground">
                                  Área: {operator.area}
                                </p>
                              )}
                              {operator.user_roles?.[0] && (
                                <Badge variant="secondary" className="mt-2">
                                  {operator.user_roles[0].role}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      <CustomerFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            fetchCustomerData();
          }
        }}
        customer={customer}
      />
    </DashboardLayout>
  );
}
